import { lazy, Suspense } from 'react'
import { Routes, Route, useNavigate, Navigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toaster } from '@/components/ui/toaster'
import { Button } from '@/components/ui/button'
import { AuthProvider, useAuth } from '@/features/auth/AuthContext'
import { LoginPage } from '@/features/auth/LoginPage'
import { AdminLayout, AdminTenantsPage } from '@/features/admin'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import { AppLayout } from '@/components/layout/AppLayout'
import { DashboardPage } from '@/features/dashboard/DashboardPage'

// Lazy-loaded pages for code splitting
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const LocationsPage = lazy(() => import('@/features/locations/LocationsPage').then(m => ({ default: m.LocationsPage })))
const LocationDetailPage = lazy(() => import('@/features/locations/LocationDetailPage').then(m => ({ default: m.LocationDetailPage })))
const CompaniesPage = lazy(() => import('@/features/companies/CompaniesPage').then(m => ({ default: m.CompaniesPage })))
const HostsPage = lazy(() => import('@/features/hosts/HostsPage').then(m => ({ default: m.HostsPage })))
const EmployeesPage = lazy(() => import('@/features/employees/EmployeesPage').then(m => ({ default: m.EmployeesPage })))
const VisitCategoriesPage = lazy(() => import('@/features/visit-categories/VisitCategoriesPage').then(m => ({ default: m.VisitCategoriesPage })))
const CustomFieldsPage = lazy(() => import('@/features/custom-fields/CustomFieldsPage').then(m => ({ default: m.CustomFieldsPage })))
const VisitsPage = lazy(() => import('@/features/visits/VisitsPage').then(m => ({ default: m.VisitsPage })))
const GatehousePage = lazy(() => import('@/features/gatehouse/GatehousePage').then(m => ({ default: m.GatehousePage })))
const AuditLogPage = lazy(() => import('@/features/audit-log/AuditLogPage').then(m => ({ default: m.AuditLogPage })))
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage').then(m => ({ default: m.ReportsPage })))
const VisitTemplatesPage = lazy(() => import('@/features/visit-templates/VisitTemplatesPage').then(m => ({ default: m.VisitTemplatesPage })))
const SelfRegistrationPage = lazy(() => import('@/features/self-registration/SelfRegistrationPage').then(m => ({ default: m.SelfRegistrationPage })))

function PageLoader() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-muted-foreground">{t('common.loading')}</div>
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Super Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/tenants" replace />} />
            <Route path="tenants" element={<AdminTenantsPage />} />
          </Route>

          {/* Tenant App */}
          <Route
            path="/app"
            element={
              <ProtectedRoute
                allowedRoles={['tenant_admin', 'manager', 'operator', 'gatehouse', 'viewer']}
                requireTenant
              >
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="locations" element={<LocationsPage />} />
            <Route path="locations/:locationId" element={<LocationDetailPage />} />
            <Route path="visits" element={<VisitsPage />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="hosts" element={<HostsPage />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="visit-categories" element={<VisitCategoriesPage />} />
            <Route path="custom-fields" element={<CustomFieldsPage />} />
            <Route path="gatehouse" element={<GatehousePage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="templates" element={<VisitTemplatesPage />} />
            <Route path="reports" element={<ReportsPage />} />
          </Route>

          {/* Public self-registration page (no auth required) */}
          <Route path="/register/:token" element={<SelfRegistrationPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <Toaster />
    </AuthProvider>
  )
}

function HomePage() {
  const { t } = useTranslation()
  const { user, currentTenant, tenants, loading, tenantsLoading, tenantsFetched, signOut, hasGlobalRole } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  // Wait for auth and tenants to fully load before showing content
  if (loading || tenantsLoading || (user && !tenantsFetched)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <ThemeToggle />
        <LanguageSwitcher />
      </div>
      <div className="text-center space-y-6 max-w-md">
        <div>
          <h1 className="text-4xl font-bold">{t('app.name')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('auth.welcome', { name: user.user_metadata?.name || user.email })}
          </p>
        </div>

        {currentTenant ? (
          <div className="bg-muted rounded-lg p-4 text-left space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('home.company')}</span>
              <span className="font-medium">{currentTenant.tenant.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('home.role')}</span>
              <span className="font-medium capitalize">
                {currentTenant.role.replace('_', ' ')}
              </span>
            </div>
          </div>
        ) : (
          <div className="bg-destructive/10 text-destructive rounded-lg p-4">
            <p className="font-medium">{t('home.noCompany')}</p>
            <p className="text-sm mt-1">
              {t('home.noCompanyMessage')}
            </p>
          </div>
        )}

        {tenants.length > 1 && (
          <div className="text-sm text-muted-foreground">
            {t('home.multipleCompanies', { count: tenants.length })}
          </div>
        )}

        <div className="flex flex-col gap-2">
          {currentTenant && (
            <Link to="/app">
              <Button variant="default" className="w-full">
                {t('navigation.goToDashboard')}
              </Button>
            </Link>
          )}
          {hasGlobalRole('super_admin') && (
            <Link to="/admin">
              <Button variant="outline" className="w-full">
                {t('navigation.administration')}
              </Button>
            </Link>
          )}
          <Button variant="ghost" onClick={handleLogout}>
            {t('auth.logout')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function NotFoundPage() {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold">{t('pages.notFound.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('pages.notFound.message')}</p>
      </div>
    </div>
  )
}

export default App
